/**
 * Phase 66 Plan 04 — Full `/api/v1/machines` endpoint catalog.
 *
 * Plan 03 added only listMachines + getMachine; Plan 04 extends with the
 * full operations surface used by `MachinesPage` + `MachineDetailPage`:
 * probe / cfg-ini ops / reprovision / recovery-mode / lsusb / delete.
 *
 * SECURITY: every cpu_id segment goes through encodeURIComponent
 * (T-66-04-02 path-traversal mitigation per PATTERNS §URL path encoding).
 *
 * @see PATTERNS.md §API endpoints catalog
 */

import { apiDel, apiGet, apiPatch, apiPost, apiPut } from "./client";
import type { Machine } from "@/types/api";

// ---- List + read ----

/** GET /api/v1/machines (optionally scoped by ?circuit_id=). */
export const listMachines = (circuitId?: string | null): Promise<Machine[]> => {
  const qs = circuitId ? `?circuit_id=${encodeURIComponent(circuitId)}` : "";
  return apiGet<Machine[]>(`/machines${qs}`);
};

/** GET /api/v1/machines/{cpu_id}. */
export const getMachine = (cpuId: string): Promise<Machine> =>
  apiGet<Machine>(`/machines/${encodeURIComponent(cpuId)}`);

// ---- Probe ----

export interface ProbeResult {
  reachable: boolean;
  latency_ms?: number;
  error?: string;
}

/** GET /api/v1/machines/{cpu_id}/probe/{offset}. */
export const probeDevice = (
  cpuId: string,
  offset: number | string,
): Promise<ProbeResult> =>
  apiGet<ProbeResult>(
    `/machines/${encodeURIComponent(cpuId)}/probe/${encodeURIComponent(String(offset))}`,
  );

// ---- Reprovision ----

/** POST /api/v1/machines/{cpu_id}/reprovision. */
export const reprovisionMachine = (cpuId: string): Promise<unknown> =>
  apiPost(`/machines/${encodeURIComponent(cpuId)}/reprovision`, {});

// ---- Recovery mode (Phase 63) ----

export interface RecoveryModeResult {
  recovery_until: string;
  duration_seconds: number;
}

/** POST /api/v1/machines/{cpu_id}/recovery-mode. */
export const setRecoveryMode = (
  cpuId: string,
  durationSeconds: number,
): Promise<RecoveryModeResult> =>
  apiPost<RecoveryModeResult>(
    `/machines/${encodeURIComponent(cpuId)}/recovery-mode`,
    { duration_seconds: durationSeconds },
  );

// ---- Force recovery window (Phase 73 D-AF-03 / D-AF-06) ----

export interface ForceRecoveryWindowResult {
  correlation_id: string;
  recovery_until: string;
  cpu_id: string;
}

/**
 * POST /api/v1/admin/machines/{cpu_id}/recovery-window/open — Phase 73 D-AF-03.
 *
 * Operator escape hatch when auth_fail_detector is rate-limited out of
 * auto-rotation. Backend RBAC: super_admin OR circuit_admin scoped to
 * machine.circuit_id. Backend validates duration_minutes (10..1440) +
 * reason (1..500). Cross-circuit circuit_admin returns 404 (T-73-04-05
 * info-disclosure mitigation — see Plan 73-04 SUMMARY).
 */
export const forceRecoveryWindow = (
  cpuId: string,
  durationMinutes: number,
  reason: string,
): Promise<ForceRecoveryWindowResult> =>
  apiPost<ForceRecoveryWindowResult>(
    `/admin/machines/${encodeURIComponent(cpuId)}/recovery-window/open`,
    { duration_minutes: durationMinutes, reason },
  );

// ---- Close recovery window (Phase 74 D-RW-01) ----

export interface CloseRecoveryWindowResult {
  correlation_id: string;
  previous_recovery_until: string | null;
  cpu_id: string;
}

/**
 * POST /api/v1/admin/machines/{cpu_id}/recovery-window/close — Phase 74 D-RW-01.
 *
 * Symmetric counterpart to `forceRecoveryWindow`. super_admin only per
 * D-RW-02 (backend re-validates the JWT role claim — SPA gate is
 * defense-in-depth). Used by `CloneApplyResultPanel` "Forza" button to
 * unblock a kiosk_auth_unstable clone-apply retry: the operator closes
 * the recovery window, the existing per-row retry then re-fires the
 * single-cpu clone-apply through the normal flow.
 *
 * Backend contract (Plan 74-01):
 *   - Request body: { reason: str (1..500) }
 *   - 202 → { correlation_id, previous_recovery_until: ISO|null, cpu_id }
 *   - 400 invalid cpu_id, 403 super_admin_required, 404 unknown/cross-circuit,
 *     422 reason validation, 429 rate-limit, 503 detector not configured.
 */
export const closeRecoveryWindow = (
  cpuId: string,
  reason: string,
): Promise<CloseRecoveryWindowResult> =>
  apiPost<CloseRecoveryWindowResult>(
    `/admin/machines/${encodeURIComponent(cpuId)}/recovery-window/close`,
    { reason },
  );

// ---- lsusb (Phase 47 Tier on-demand) ----

export interface LsusbResult {
  output: string;
  devices?: { vendor: string; product: string; description?: string }[];
}

/** POST /api/v1/machines/{cpu_id}/lsusb. */
export const triggerLsusb = (cpuId: string): Promise<LsusbResult> =>
  apiPost<LsusbResult>(`/machines/${encodeURIComponent(cpuId)}/lsusb`, {});

// ---- cfg.ini ops (Phase 63 TIER 2) ----

export interface CfgIniState {
  raw: string;
  has_value: boolean;
  size: number;
  sha256: string | null;
}

export interface CfgIniSnapshot {
  version_id: string;
  ts: string;
  size: number;
  sha256: string;
  is_latest?: boolean;
}

/** GET /api/v1/machines/{cpu_id}/cfg-ini. */
export const getCfgIni = (cpuId: string): Promise<CfgIniState> =>
  apiGet<CfgIniState>(`/machines/${encodeURIComponent(cpuId)}/cfg-ini`);

/** PUT /api/v1/machines/{cpu_id}/cfg-ini — DB persist + S3 push + MQTT config_update. */
export const putCfgIni = (cpuId: string, raw: string): Promise<unknown> =>
  apiPut(`/machines/${encodeURIComponent(cpuId)}/cfg-ini`, { raw });

/**
 * POST /pull result — since the 2026-06-12 no-clobber fix the backend
 * persists ONLY when the DB twin is empty (seed). `diff=true` means the
 * kiosk's live file differs from the server: nothing was saved and
 * `pulled_raw` carries the kiosk content for the admin to inspect/accept.
 */
export interface CfgIniPullResult {
  cpu_id: string;
  sha256: string;
  size: number;
  persisted?: boolean;
  diff?: boolean;
  db_sha256?: string | null;
  pulled_raw?: string | null;
}

/** POST /api/v1/machines/{cpu_id}/cfg-ini/pull — request fresh blob from kiosk. */
export const pullCfgIni = (cpuId: string): Promise<CfgIniPullResult> =>
  apiPost<CfgIniPullResult>(
    `/machines/${encodeURIComponent(cpuId)}/cfg-ini/pull`,
    {},
  );

/** GET /api/v1/machines/{cpu_id}/cfg-ini/snapshots. */
export const listCfgIniSnapshots = (
  cpuId: string,
): Promise<{ snapshots: CfgIniSnapshot[] } | CfgIniSnapshot[]> =>
  apiGet(`/machines/${encodeURIComponent(cpuId)}/cfg-ini/snapshots`);

/** GET /api/v1/machines/{cpu_id}/cfg-ini/snapshots/{version_id}. */
export const getCfgIniSnapshot = (
  cpuId: string,
  versionId: string,
): Promise<CfgIniState> =>
  apiGet<CfgIniState>(
    `/machines/${encodeURIComponent(cpuId)}/cfg-ini/snapshots/${encodeURIComponent(versionId)}`,
  );

/** POST /api/v1/machines/{cpu_id}/cfg-ini/restore — re-apply a historical version_id. */
export const restoreCfgIniSnapshot = (
  cpuId: string,
  versionId: string,
): Promise<unknown> =>
  apiPost(`/machines/${encodeURIComponent(cpuId)}/cfg-ini/restore`, {
    version_id: versionId,
  });

// ---- PATCH machine (Phase 68 — lat/lon for sunrise/sunset overlay) ----

/**
 * Body accepted by `PATCH /api/v1/machines/{cpu_id}` per Plan 68-02
 * alembic 017 + machines.py extension. lat/lon are nullable; passing
 * `null` clears the column (used by MachineLocationPicker "Cancella
 * posizione" destructive flow).
 *
 * Range validation is defense-in-depth: Pydantic Field(ge=, le=) on
 * the backend + Postgres CHECK constraints (machines_lat_range /
 * machines_lon_range). The picker also clamps lat/lon client-side via
 * `<input type="number" min/max>` so the user sees the constraint
 * immediately instead of round-tripping a 422.
 */
export interface MachinePatchBody {
  lat?: number | null;
  lon?: number | null;
  /**
   * Phase 70 Plan 04 — free-text description (max 200 chars).
   * Backend Pydantic Field validates length.
   */
  description?: string | null;
  /**
   * Phase 70 Plan 04 — cluster tag (max 64 chars, no leading/trailing
   * whitespace). Backend field_validator mirrors the DB CHECK constraint.
   */
  cluster_label?: string | null;
  /**
   * Phase 70 Plan 04 — service image tag overrides. JSONB sub-key merge:
   * a PATCH that touches only one tag does NOT remove other previously
   * set tags (idempotent partial updates).
   */
  service_image_tags?: Record<string, string> | null;
  /**
   * Phase 77a hotfix — friendly machine name (max 128 chars). Empty
   * string is normalized to NULL server-side so the SPA's "clear back to
   * cpu_id fallback" flow works with an empty submit.
   */
  name?: string | null;
  /**
   * 2026-06-11 — cross-circuit move. super_admin only at the backend
   * (403 from anyone else). NULL clears the FK back to the legacy "no
   * circuit" state. The PATCH response surfaces the saved value so the
   * SPA cache can refresh without a separate GET.
   */
  circuit_id?: string | null;
  /**
   * Phase 80 Plan 01 (ANAG-01..04) — machine anagrafica / install-site
   * fields. All nullable: passing `null` (or an empty input normalized to
   * null) clears the column. country_code is the ISO 3166-1 NUMERIC code
   * (number, e.g. 380 = Italy), NOT the alpha-2 string. The backend
   * line-749 allow-list (B-1, landed by Plan 80-01) is what actually
   * persists these — pydantic alone would silently drop them.
   *
   * Edited by the Plan 80-04b MachineAnagraficaForm.
   */
  serial_number?: string | null;
  model?: string | null;
  address?: string | null;
  zip_code?: string | null;
  city?: string | null;
  country_code?: number | null;
}

/** PATCH /api/v1/machines/{cpu_id} — update lat/lon. */
export const patchMachineLocation = (
  cpuId: string,
  body: MachinePatchBody,
): Promise<Machine> =>
  apiPatch<Machine>(`/machines/${encodeURIComponent(cpuId)}`, body);

/**
 * Phase 70 Plan 05 — generic PATCH /api/v1/machines/{cpu_id}.
 *
 * Same endpoint as `patchMachineLocation` (the backend accepts every
 * MachinePatchBody field), exposed under a more general name for the
 * MachineDetailPage description/cluster_label/service_image_tags edit
 * form. Kept separate from `patchMachineLocation` so existing
 * MachineLocationPicker call sites don't need to refactor.
 */
export const patchMachine = (
  cpuId: string,
  body: MachinePatchBody,
): Promise<Machine> =>
  apiPatch<Machine>(`/machines/${encodeURIComponent(cpuId)}`, body);

// ---- Delete (Phase 56) ----

export interface DeleteMachineBody {
  include_minio?: boolean;
  force_online?: boolean;
  /** Phase 72 D-DT-16 — opt-in purge of the machine's digital twin
   *  (rows + per-version rows + MinIO prefix). Default false per the
   *  D-DT-10 safety net (preserve twin so auto-restore stays viable). */
  clear_digital_twin?: boolean;
}

export interface DeleteMachineStep {
  name: string;
  status: "ok" | "error" | "skipped" | "already_absent" | string;
  detail?: string;
}

export interface DeleteMachineResponse {
  cpu_id: string;
  status: "completed" | "partial" | string;
  steps: DeleteMachineStep[];
}

/** DELETE /api/v1/machines/{cpu_id}. */
export const deleteMachine = (
  cpuId: string,
  body: DeleteMachineBody = {},
): Promise<DeleteMachineResponse> =>
  apiDel<DeleteMachineResponse>(
    `/machines/${encodeURIComponent(cpuId)}`,
    body,
  );

// ---- Crash reports (Binario-2 blackbox) ----

export interface CrashReportItem {
  crash_id: string;
  status: string; // pending_upload | uploaded | upload_failed
  reason: string | null;
  detected_at: string | null;
  boot_id: string | null;
  prev_boot_id: string | null;
  prev_uptime_s: number | null;
  snapshot_count: number | null;
  watchdog_bootstatus: number | null;
  error_detail: string | null;
  acknowledged_at: string | null;
  created_at: string;
}

export interface CrashDownloadResponse {
  url: string;
  expires_in_seconds: number;
}

/** GET /api/v1/machines/{cpu_id}/crash-reports. */
export const listCrashReports = (cpuId: string): Promise<CrashReportItem[]> =>
  apiGet<CrashReportItem[]>(
    `/machines/${encodeURIComponent(cpuId)}/crash-reports`,
  );

/** GET /api/v1/machines/{cpu_id}/crash-reports/{crash_id}/download. */
export const downloadCrashReport = (
  cpuId: string,
  crashId: string,
): Promise<CrashDownloadResponse> =>
  apiGet<CrashDownloadResponse>(
    `/machines/${encodeURIComponent(cpuId)}/crash-reports/${encodeURIComponent(crashId)}/download`,
  );

/** POST /api/v1/machines/{cpu_id}/crash-reports/ack. */
export const ackCrashReports = (
  cpuId: string,
): Promise<{ acknowledged: number }> =>
  apiPost<{ acknowledged: number }>(
    `/machines/${encodeURIComponent(cpuId)}/crash-reports/ack`,
    {},
  );
